'use client';
import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';
import { BigWinnerPod } from '../../BigWinnerPod/BigWinnerPod';
import { BigWinnerPodSkeleton } from '../../BigWinnerPod/BigWinnerPodSkeleton';
import { HorizontalScrollContainer } from '../../HorizontalScrollContainer/HorizontalScrollContainer';
import { useBigWinners } from './hooks';

type CasinoBigWinnersProps = ComponentProps<'div'>;

/**
 * CasinoBigWinners component displays a carousel of big winners.
 * Extends props of HTMLDivElement.
 * Negative margins + small padding (on scroll container) are needed to allow navigation with TAB highlight (accessibility).
 * @returns A div element with a carousel of big winners.
 */
function CasinoBigWinners({ className, ...props }: CasinoBigWinnersProps) {
  const { data, isLoading } = useBigWinners();

  if (!isLoading && data.length === 0) return null;

  const { container, innerContainer, scrollContainer } = {
    container: twMerge('ll-grid-container carousel-gap-offset', className),
    innerContainer: 'll-container-full lg:ll-container-inner',
    scrollContainer: 'px-[calc(var(--ll-container-padding)+2px)] lg:px-0.5 py-0.5 -m-0.5',
  };

  return (
    <div className={container} {...props}>
      <div className={innerContainer}>
        <HorizontalScrollContainer role="list" className={scrollContainer}>
          {isLoading
            ? Array.from({ length: 10 }).map((_, index) => <BigWinnerPodSkeleton key={index} />)
            : data.map(winner => <BigWinnerPod key={winner.id} role="listitem" {...winner} />)}
        </HorizontalScrollContainer>
      </div>
    </div>
  );
}

export { CasinoBigWinners };
